// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-12): the way out of a meeting, said once. The
 * presenter ended it, this person left, or they were removed: the meeting's
 * buttons are already gone (main.ts takes them down and lets go of the
 * room), and this card covers the page until **Close**, which opens the
 * page again without the room in its address — so a guest meets the
 * welcome page and an account the app, as on any visit.
 */

import { t } from '../i18n'

export type MeetingOver = 'ended' | 'left' | 'kicked'

/** Show the card; `close` is what Close does (by default, the page again without the room). */
export function showMeetingOver(root: HTMLElement, why: MeetingOver, close: () => void = reopenWithoutRoom): HTMLElement {
  document.querySelector('.meeting-over')?.remove()
  const card = document.createElement('div')
  card.className = 'meeting-over'
  card.setAttribute('role', 'alertdialog')
  card.setAttribute('aria-modal', 'true')
  const box = document.createElement('div')
  box.className = 'meeting-over-box'
  const text = document.createElement('p')
  text.id = 'meeting-over-text'
  text.textContent = t(why === 'ended' ? 'meeting.over.ended' : why === 'left' ? 'meeting.over.left' : 'activity.kicked')
  card.setAttribute('aria-labelledby', text.id)
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'activity-primary'
  button.textContent = t('meeting.over.close')
  button.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  button.addEventListener('click', close)
  box.append(text, button)
  card.append(box)
  root.append(card)
  button.focus()
  return card
}

/** This page's address without the room and its seat. */
export function addressWithoutRoom(href: string): string {
  const url = new URL(href)
  url.searchParams.delete('room')
  url.searchParams.delete('st')
  return url.toString()
}

function reopenWithoutRoom(): void {
  window.location.replace(addressWithoutRoom(window.location.href))
}
